import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import Box from '../Box/Box';
import ButtonCore from '../Button/ButtonCore/ButtonCore';
import Icon from '../Icon/Icon';
import Typography from '../Typography/Typography';

import './RadioButton.css';

const RADIO_ICON_STATES = {
  OUTLINE: 'radioOutline',
  HOVER: 'radioOutlineHover',
  PRESSED: 'radioOutlinePressed',
  SELECTED: 'radioOutlineSelected',
};

function RadioButton({
  id,
  name,
  title,
  description,
  value,
  radioSelection,
  onClick,
  isDisabled,
  isTruncated,
  frontIcon,
  className,
  dataTestId,
}) {
  const [iconState, setIconState] = useState(RADIO_ICON_STATES.OUTLINE);
  // if value or radioSelection are objects, note that it's a referential comparison.
  const isRadioChecked = value === radioSelection;

  const radioButtonClasses = classnames(
    'RadioButton',
    isDisabled && 'RadioButton--isDisabled',
    frontIcon && 'RadioButton--hasFrontIcon',
    isRadioChecked && 'RadioButton--isChecked',
    className
  );

  const radioOutlineIconClasses = classnames(
    'RadioButton-button',
    isDisabled && 'RadioButton-button--isDisabled'
  );

  const titleClasses = classnames(
    'RadioButton-title',
    isTruncated && 'RadioButton-title--isTruncated'
  );

  const descriptionClasses = classnames(
    'RadioButton-description',
    isTruncated && 'RadioButton-description--isTruncated'
  );

  useEffect(() => {
    setIconState(
      isRadioChecked ? RADIO_ICON_STATES.SELECTED : RADIO_ICON_STATES.OUTLINE
    );
  }, [isRadioChecked]);

  const handleMouseEnter = () =>
    setIconState(
      isRadioChecked ? RADIO_ICON_STATES.SELECTED : RADIO_ICON_STATES.HOVER
    );
  const handleMouseLeave = () =>
    setIconState(
      isRadioChecked ? RADIO_ICON_STATES.SELECTED : RADIO_ICON_STATES.OUTLINE
    );
  const handleMouseDown = () => setIconState(RADIO_ICON_STATES.PRESSED);
  const handleMouseUp = () =>
    setIconState(
      isRadioChecked ? RADIO_ICON_STATES.SELECTED : RADIO_ICON_STATES.HOVER
    );

  const handleRadioClick = (e) => {
    if (!isDisabled) {
      onClick(e, value);
      setIconState(RADIO_ICON_STATES.SELECTED);
    }
  };

  return (
    <ButtonCore
      className={radioButtonClasses}
      isDisabled={isDisabled}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onClick={(e) => {
        handleRadioClick(e);
      }}
      dataTestId={`${dataTestId}-button`}
      aria-checked={isRadioChecked}>
      <Box tag='div' className='RadioButton-content'>
        {frontIcon}
        <Box tag='div' className='RadioButton-text'>
          <Typography kind='label' htmlFor={name} className={titleClasses}>
            {title}
          </Typography>
          <Typography kind='body2' className={descriptionClasses}>
            {description}
          </Typography>
        </Box>
        <Box tag='div' className='RadioButton-input-contatiner'>
          <input
            type='radio'
            id={id}
            name={name}
            value={value}
            checked={isRadioChecked}
            onChange={() => {}}
            disabled={isDisabled}
            className='RadioButton-input'
            aria-hidden
            data-testid={`${dataTestId}-input`}
          />
          <Icon kind={iconState} className={radioOutlineIconClasses} />
        </Box>
      </Box>
      <span className='RadioButton-overlay' />
    </ButtonCore>
  );
}

RadioButton.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.bool,
    PropTypes.string,
    PropTypes.object,
  ]).isRequired,
  radioSelection: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.bool,
    PropTypes.string,
    PropTypes.object,
  ]),
  onClick: PropTypes.func,
  frontIcon: PropTypes.element,
  isDisabled: PropTypes.bool,
  isTruncated: PropTypes.bool,
  className: PropTypes.string,
  dataTestId: PropTypes.string,
};

RadioButton.defaultProps = {
  description: null,
  radioSelection: null,
  onClick: () => {},
  frontIcon: null,
  isDisabled: false,
  isTruncated: false,
  className: null,
  dataTestId: null,
};

export default RadioButton;
